import { Component, OnInit, AfterViewInit, Output, EventEmitter, HostListener, ViewChild, ElementRef } from '@angular/core';
import { Router } from '@angular/router';

import { UsersService } from '../../../core/services/users.service';
import { MENU } from '../../menu';

// The full-screen menu behind the grid button in the topbar (replaced the sidebar, Trello card 207).
// Recents: the last 7 links opened from this menu, kept in localStorage as paths and looked up in MENU
// again on every open, so a renamed or removed page never shows a stale label.
const RECENTS_KEY = 'admin_menu_recents';
const RECENTS_MAX = 7;

interface MenuLink {
    label: string;
    link: string;
    icon?: string;
    section?: string; // the tile a sub-link sits in, "Cinebot" for Queue
}

interface MenuSection {
    label: string;
    icon: string;
    link: string; // its own link, or its first sub-link
    subItems: MenuLink[];
}

@Component({
    selector: 'app-grid-menu',
    templateUrl: './grid-menu.component.html',
    styleUrls: ['./grid-menu.component.scss']
})
export class GridMenuComponent implements OnInit, AfterViewInit {

    @Output() closed = new EventEmitter();
    @ViewChild('filterInput') filterInput: ElementRef;

    query = '';
    sections: MenuSection[] = [];
    recents: MenuLink[] = [];

    private allSections: MenuSection[] = [];

    constructor(private router: Router, private usersService: UsersService) { }

    ngOnInit() {
        const access = this.usersService.user.authentication.access;

        this.allSections = MENU
            .filter(item => !item.isTitle && (!item.acl || access?.indexOf(item.acl) > -1))
            .map(item => {
                const subItems = (item.subItems || []).map(sub => ({ label: sub.label, link: sub.link }));
                return { label: item.label, icon: item.icon, link: subItems.length ? subItems[0].link : item.link, subItems };
            });
        this.sections = this.allSections;

        const links = this.links();
        this.recents = this.readRecents()
            .map(link => links.find(item => item.link === link))
            .filter(Boolean);
    }

    ngAfterViewInit() {
        // Typing filters straight away with a mouse; on a phone the keyboard would cover the grid
        if (window.matchMedia('(pointer: fine)').matches) {
            this.filterInput.nativeElement.focus();
        }
    }

    @HostListener('document:keydown.escape')
    close() {
        this.closed.emit();
    }

    filter(query: string) {
        this.query = query;
        const q = query.trim().toLowerCase();

        this.sections = this.allSections
            .map(section => {
                if (!q || section.label.toLowerCase().includes(q)) {
                    return section;
                }
                const subItems = section.subItems.filter(sub => sub.label.toLowerCase().includes(q));
                return subItems.length ? { ...section, link: subItems[0].link, subItems } : null;
            })
            .filter(Boolean);
    }

    // Enter in the filter opens the first match
    openFirst() {
        const section = this.sections[0];
        if (section && this.query.trim()) {
            this.remember(section.link);
            this.router.navigateByUrl(section.link);
            this.close();
        }
    }

    // On every link click; routerLink does the navigating. A ctrl/cmd/shift click opens a new tab and leaves the menu open.
    open(link: string, event: MouseEvent) {
        this.remember(link);
        if (!event.ctrlKey && !event.metaKey && !event.shiftKey) {
            this.close();
        }
    }

    trackByLink(index: number, item: MenuSection | MenuLink) {
        return item.link;
    }

    // Every page in the menu, a sub-link with its tile's icon
    private links(): MenuLink[] {
        const links: MenuLink[] = [];
        for (const section of this.allSections) {
            if (!section.subItems.length) {
                links.push({ label: section.label, link: section.link, icon: section.icon });
            }
            for (const sub of section.subItems) {
                links.push({ ...sub, icon: section.icon, section: sub.label === section.label ? undefined : section.label });
            }
        }
        return links;
    }

    // Built on the recents as shown, so a link no longer in the menu drops out instead of keeping a place
    private remember(link: string) {
        const recents = [link, ...this.recents.map(item => item.link).filter(item => item !== link)].slice(0, RECENTS_MAX);
        try {
            localStorage.setItem(RECENTS_KEY, JSON.stringify(recents));
        } catch (e) { }
    }

    private readRecents(): string[] {
        try {
            const recents = JSON.parse(localStorage.getItem(RECENTS_KEY) || '[]');
            return Array.isArray(recents) ? recents.filter(link => typeof link === 'string') : [];
        } catch (e) {
            return [];
        }
    }
}
